/* Customer consent belongs to the exact Studio designs in the current cart. */
(() => {
  if (globalThis.StudioCartApproval) return;
  const APPROVED = 'Engraving designs approved', FINGERPRINT = 'Engraving designs approval fingerprint';
  // Shopify Liquid serializes line-item properties as pairs; Ajax returns a map.
  function properties(item) {
    const value = item.properties ?? {};
    if (!Array.isArray(value)) { if (typeof value !== 'object') fail('unavailable'); return value; }
    const keys = new Set();
    for (const pair of value) {
      if (!Array.isArray(pair) || pair.length !== 2 || typeof pair[0] !== 'string' || keys.has(pair[0])) fail('unavailable');
      keys.add(pair[0]);
    }
    return Object.fromEntries(value);
  }
  const property = (item, name) => { const values = properties(item); return values[`_${name}`] || values[name]; };
  const isStudio = item => ['Design file', 'Engraving files', 'Studio submission'].some(name => Boolean(property(item, name)));
  const fail = (code, cart) => { throw Object.assign(new Error(code), { code, cart }); };
  function snapshot(cart) {
    if (!Array.isArray(cart?.items)) fail('unavailable');
    const items = cart.items.filter(isStudio).map(item => {
      if (typeof item.key !== 'string' || !item.key || !Number.isInteger(item.quantity) || item.quantity < 1) fail('unavailable');
      return [item.key, String(item.product_id), String(item.variant_id), item.quantity,
        Object.entries(properties(item)).sort(([a], [b]) => a.localeCompare(b))];
    }).sort((a, b) => a[0].localeCompare(b[0]));
    return items.length ? JSON.stringify(items) : '';
  }
  async function fingerprint(value) {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
    return `sha256:${Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('')}`;
  }
  // Older mobile browsers have AbortController but no AbortSignal.timeout.
  function requestDeadline(milliseconds) {
    if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') return { signal: AbortSignal.timeout(milliseconds), clear() {} };
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), milliseconds);
    return { signal: controller.signal, clear: () => clearTimeout(timer) };
  }
  function createClient({ fetchImpl = fetch, root = '/', label = 'I approve all of these designs' } = {}) {
    const base = /^\/(?:[a-zA-Z0-9_-]+\/)*$/.test(root) ? root : '/';
    let tail = Promise.resolve();
    const serial = work => { const task = tail.then(work, work); tail = task.catch(() => {}); return task; };
    async function request(path, options = {}) {
      const deadline = requestDeadline(15000);
      try {
        const response = await fetchImpl(`${base}${path}`, { credentials: 'same-origin', cache: 'no-store', ...options, signal: deadline.signal });
        if (!response.ok) fail('unavailable');
        const cart = await response.json(); snapshot(cart); return cart;
      } finally { deadline.clear(); }
    }
    const update = attributes => request('cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ attributes }) });
    const clear = () => update({ [APPROVED]: '', [FINGERPRINT]: '' });
    return {
      read: () => request('cart.js'),
      clear: () => serial(clear),
      approve: expected => serial(async () => {
        const current = await request('cart.js');
        if (!expected || snapshot(current) !== expected) fail('changed', current);
        const hash = await fingerprint(expected);
        const approved = await update({ [APPROVED]: label, [FINGERPRINT]: hash });
        if (snapshot(approved) !== expected) { await clear(); fail('changed', approved); }
        if (approved.attributes?.[APPROVED] !== label || approved.attributes?.[FINGERPRINT] !== hash) fail('unavailable');
        return approved;
      })
    };
  }
  const api = { APPROVED, FINGERPRINT, isStudio, snapshot, fingerprint, createClient };
  globalThis.StudioCartApproval = api;
  if (typeof document === 'undefined') return;

  const all = selector => [...document.querySelectorAll(selector)];
  let client, strings, cart, current = '', checked = null, pass = null, revision = 0, busy = false, reading = null, queued = false, message = '';
  function initialize() {
    if (client) return true;
    const source = document.querySelector('[data-studio-approval-config]'); if (!source) return false;
    try {
      const config = JSON.parse(source.textContent); strings = config.strings; cart = config.cart; current = snapshot(cart);
      client = createClient({ root: window.Shopify?.routes?.root || '/', label: strings.label }); return true;
    } catch { return false; }
  }
  const quantityPending = () => all('cart-items, cart-drawer-items').some(node => node.quantityUpdatePending);
  function formMatchesCart(form) {
    if (!form || !cart) return false;
    const rows = [...form.querySelectorAll('[data-line-key]')], seen = new Set();
    return rows.length === cart.items.length && rows.every(row => {
      const item = cart.items.find(item => item.key === row.dataset.lineKey), input = row.querySelector('[name="updates[]"]');
      if (!item || seen.has(item.key) || !input || !String(input.value).trim() || Number(input.value) !== item.quantity) return false;
      seen.add(item.key); return true;
    });
  }
  function clearStored() {
    if (!client || !busy && !cart?.attributes?.[APPROVED] && !cart?.attributes?.[FINGERPRINT]) return;
    client.clear().then(value => { if (cart) cart.attributes = value.attributes; }).catch(() => {});
  }
  function invalidate() { checked = null; pass = null; revision++; message = ''; clearStored(); paint(); }
  function accept(value) {
    const next = snapshot(value), changed = next !== current;
    cart = value; current = next;
    if (changed) invalidate();
    paint();
  }
  function paint() {
    if (!initialize()) return;
    const required = Boolean(current), blocked = required && (checked !== current || busy || reading || quantityPending());
    for (const root of all('[data-studio-approval-root]')) {
      root.hidden = !required;
      const input = root.querySelector('[data-studio-cart-approval]'); input.checked = required && checked === current; input.disabled = Boolean(busy || reading || quantityPending() || required && !formMatchesCart(input.form));
      const error = root.querySelector('[data-studio-approval-error]'); error.textContent = message; error.hidden = !message;
      input.setAttribute('aria-invalid', String(Boolean(message)));
    }
    for (const button of all('[data-studio-approval-checkout]')) {
      if (blocked || required && !formMatchesCart(button.form)) { button.disabled = true; button.dataset.studioApprovalBlocked = 'true'; }
      else if (button.dataset.studioApprovalBlocked === 'true') { delete button.dataset.studioApprovalBlocked; button.disabled = !cart.items.length || button.dataset.studioUpgradeBlocked === 'true' || button.dataset.bulkOrderBlocked === 'true'; }
      if (button.dataset.bulkOrderBlocked === 'true') button.disabled = true;
      button.setAttribute('aria-busy', String(busy));
    }
    // Studio orders use the standard Checkout action so approval is recorded
    // before any payment handoff. Ordinary carts retain accelerated checkout.
    for (const node of all('[data-studio-approval-accelerated]')) { node.dataset.studioApprovalHidden = String(required); node.hidden = required || all('[data-studio-upgrade-checkout]').some(button => button.dataset.studioUpgradeBlocked === 'true') || all('[name="checkout"]').some(button => button.dataset.bulkOrderBlocked === 'true'); node.inert = node.hidden; }
  }
  async function refresh() {
    if (!initialize() || reading) return reading;
    reading = client.read().then(accept).catch(() => { checked = null; pass = null; message = strings.error; }).finally(() => { reading = null; paint(); });
    paint(); return reading;
  }
  Object.assign(api, { accept: value => { if (initialize()) { try { if (!Number.isInteger(value?.item_count)) { refresh(); return; } accept(value); } catch { refresh(); } } }, finishCheckout: () => { pass = null; }, sync: paint, refresh });
  document.addEventListener('change', event => {
    if (!event.target.matches?.('[data-studio-cart-approval]') || !initialize()) return;
    checked = event.target.checked && !quantityPending() && formMatchesCart(event.target.form) ? current : null; pass = null; revision++; message = '';
    if (!checked) clearStored(); paint();
  });
  function changedLine(event) {
    if (!initialize() || !current) return;
    const row = event.target.closest?.('[data-line-key]');
    const item = row && cart.items.find(item => item.key === row.dataset.lineKey);
    if (item && isStudio(item)) invalidate();
  }
  document.addEventListener('input', event => { if (event.target.matches?.('[name="updates[]"]')) changedLine(event); }, true);
  document.addEventListener('change', event => { if (event.target.matches?.('[name="updates[]"]')) changedLine(event); }, true);
  document.addEventListener('click', event => { if (event.target.closest?.('cart-remove-button')) changedLine(event); }, true);
  // Window capture precedes the second-side guard. Its verified resubmission
  // may pass through this handler again without creating another approval write.
  window.addEventListener('submit', async event => {
    if (!['cart', 'CartDrawer-Form'].includes(event.target.id) || event.submitter?.name !== 'checkout' || !initialize() || !current) return;
    if (pass === current && checked === current && !busy && !quantityPending() && formMatchesCart(event.target)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (busy || reading) return;
    if (quantityPending() || !formMatchesCart(event.target)) { message = strings.error; paint(); return; }
    if (checked !== current) {
      message = strings.required; paint();
      all('[data-studio-cart-approval]').find(input => input.form === event.target)?.focus(); return;
    }
    const expected = current, generation = revision, submitter = event.submitter;
    busy = true; message = ''; paint();
    try {
      const confirmed = await client.approve(expected);
      accept(confirmed);
      if (generation !== revision || checked !== expected || quantityPending() || !formMatchesCart(event.target)) { clearStored(); return; }
      pass = expected; busy = false; paint();
      const quantities = [...event.target.querySelectorAll('[name="updates[]"]')].filter(input => !input.disabled);
      quantities.forEach(input => { input.disabled = true; });
      try { event.target.requestSubmit(submitter); }
      finally { quantities.forEach(input => { input.disabled = false; }); if (!globalThis.StudioCartUpgrades?.checkingCheckout && !globalThis.JaxBulkCart?.checkingCheckout) pass = null; }
    } catch (error) {
      if (error.cart) accept(error.cart);
      if (error.code === 'changed') { checked = null; pass = null; }
      message = error.code === 'changed' ? strings.required : strings.error;
    } finally { busy = false; paint(); }
  }, true);
  function boot() {
    if (!initialize()) return;
    paint();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, event => event.cartData?.items ? api.accept(event.cartData) : refresh());
    new MutationObserver(records => {
      if (queued || !records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches?.('[data-studio-approval-root]') || node.querySelector?.('[data-studio-approval-root]'))))) return;
      queued = true; queueMicrotask(() => { queued = false; refresh(); });
    }).observe(document.body, { childList: true, subtree: true });
  }
  window.addEventListener('pageshow', event => { if (event.persisted) { invalidate(); refresh(); } });
  document.addEventListener('visibilitychange', () => { if (!document.hidden && client) refresh(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true }); else boot();
})();

